import { useState, useEffect, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Play, Eye, Clock, ArrowLeft, Share2, CheckCircle, Link2 } from 'lucide-react'
import { api } from '@/lib/api'
import { getTeacherName, formatDate, resolveImg } from '@/lib/helpers'
import VideoPlayer from '@/components/VideoPlayer'

const API_BASE = import.meta.env.VITE_API_URL || ''

export default function VideoDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [video, setVideo] = useState<any>(null)
  const [relatedVideos, setRelatedVideos] = useState<any[]>([])
  const [shareToast, setShareToast] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }
      document.removeEventListener('click', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

  useEffect(() => {
    if (!id) return
    api.videos.getById(id).then(setVideo).catch(console.error)
    api.videos.getRelated(id).then(setRelatedVideos).catch(console.error)
    api.videos.incrementView(id).catch(() => {})
  }, [id])
  
  const handleNativeShare = async () => {
    setDropdownOpen(false)
    const url = `${window.location.origin}/videos/${id}`
    try {
      await navigator.share({ title: video.title, url })
    } catch {}
  }

  const handleCopyLink = async () => {
    setDropdownOpen(false)
    const url = `${window.location.origin}/videos/${id}`
    try {
      await navigator.clipboard.writeText(url)
      setShareToast(true)
      setTimeout(() => setShareToast(false), 3000)
    } catch {
      window.prompt('Холбоосыг хуулна уу:', url)
    }
  }



  if (!video) return <div className="max-w-[1024px] mx-auto px-4 py-16 text-center text-gray-400">Ачаалж байна...</div>

  const teacherName = getTeacherName(video)
  const teacherTitle = video.teacherId?.title || ''
  const teacherId = video.teacherId?._id
  const videoSrc = resolveImg(
    (video.transcodingStatus === 'completed' && video.hlsUrl) ? video.hlsUrl : video.videoUrl
  )
  const poster = resolveImg(video.thumbnail || video.thumbnailUrl)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200">
        <div className="max-w-[1024px] mx-auto px-4 flex items-center justify-between py-4">
          <button
            onClick={() => navigate(-1)}
            className="bg-white border border-gray-200 rounded-full px-4 py-2.5 flex items-center gap-2.5 text-sm font-medium text-gray-800 shadow-md hover:bg-gray-50 transition cursor-pointer"
          >
            <ArrowLeft className="size-5" />
            Буцах
          </button>
          <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setDropdownOpen((v) => !v)}
            className="bg-white border border-gray-200 rounded-full p-2.5 shadow-md hover:bg-gray-50 transition cursor-pointer"
          >
            <Share2 className="size-5 text-gray-600" />
          </button>
          {dropdownOpen && (
              <div className="absolute right-0 top-12 w-48 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                {typeof navigator.share === 'function' && (
                  <button
                    onClick={handleNativeShare}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    <Share2 className="size-4 text-gray-400" />
                    Хуваалцах
                  </button>
                )}
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                >
                  {shareToast ? (
                    <>
                      <span className="size-4 text-green-500">✓</span>
                      Хуулагдлаа
                    </>
                  ) : (
                    <>
                      <Link2 className="size-4 text-gray-400" />
                      Линк хуулах
                    </>
                  )}
                </button>
              </div>
            )}
            </div>
        </div>
      </div>

      {/* Share toast */}
      {shareToast && (
        <div
          className="fixed top-20 left-1/2 -translate-x-1/2 z-50"
          style={{ animation: 'shareToastIn 0.25s ease-out' }}
        >
          <div className="bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-full shadow-lg flex items-center gap-2">
            <CheckCircle className="size-4 text-green-400" />
            Холбоос хуулагдлаа
          </div>
        </div>
      )}
      <style>{`
        @keyframes shareToastIn {
          from { opacity: 0; transform: translate(-50%, -8px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>

      <div className="max-w-[1024px] mx-auto px-4 py-6 pb-16">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            {/* Video Player */}
            {videoSrc ? (
              <VideoPlayer src={videoSrc} poster={poster} title={video.title} />
            ) : (
              <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-950 rounded-2xl relative flex items-center justify-center overflow-hidden">
                {poster && (
                  <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-black/30" />
                <div className="relative z-10 flex flex-col items-center gap-3">
                  <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Play className="size-9 text-white ml-1" />
                  </div>
                  <p className="text-white/70 text-sm">Видео удахгүй нэмэгдэнэ</p>
                </div>
                <span className="absolute bottom-4 right-4 bg-black/70 text-white text-sm px-3 py-1 rounded-lg z-10">{video.duration}</span>
              </div>
            )}

            <div className="mt-6">
              <h1 className="font-display font-black text-2xl text-gray-900">{video.title}</h1>
              {video.isMemberOnly && (
                <span className="inline-block mt-2 bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full uppercase">
                  Зөвхөн гишүүдэд
                </span>
              )}
              <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                <span className="flex items-center gap-1"><Eye className="size-4" />{video.viewCount?.toLocaleString()} үзсэн</span>
                <span className="flex items-center gap-1"><Clock className="size-4" />{video.duration}</span>
                <span>{formatDate(video.publishedAt)}</span>
              </div>
            </div>

            <div className="flex items-center gap-4 mt-6 pt-6 border-t border-gray-200">
              <div className="w-12 h-12 rounded-full bg-gray-200 shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">{teacherName}</h3>
                <p className="text-sm text-gray-500">{teacherTitle}</p>
              </div>
              {teacherId && (
                <Link to={`/teachers/${teacherId}`} className="text-sm text-primary-500 hover:text-primary-600 font-medium ml-auto">
                  Дэлгэрэнгүй үзэх
                </Link>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <h2 className="font-display font-bold text-lg text-gray-900 mb-3">Тайлбар</h2>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{video.description}</p>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-400">Энэ видеоны зохиогчийн эрх хамгаалагдсан. Зөвшөөрөлгүйгээр хуулбарлах, тарааж болохгүй.</p>
            </div>
          </div>

          <div className="w-full lg:w-[300px] shrink-0">
            <h2 className="font-display font-bold text-lg text-gray-900 mb-4">Бусад видео</h2>
            <div className="space-y-4">
              {relatedVideos.map(rv => (
                <Link key={rv._id} to={`/videos/${rv._id}`} className="flex gap-3 group">
                  <div className="w-32 h-20 bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl shrink-0 relative flex items-center justify-center overflow-hidden">
                    {resolveImg(rv.thumbnail || rv.thumbnailUrl) && (
                      <img src={resolveImg(rv.thumbnail || rv.thumbnailUrl)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    )}
                    <Play className="size-6 text-white/50 group-hover:text-white/80 transition relative z-10" />
                    <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1 py-0.5 rounded z-10">{rv.duration}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-primary-500 transition">{rv.title}</h3>
                    <p className="text-xs text-gray-500 mt-1">{getTeacherName(rv)}</p>
                    <p className="text-xs text-gray-400 mt-1">{rv.duration}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
